/**
 * SpatialIndex.ts
 * Bucketed spatial hash for fast unit lookups by tile position.
 *
 * Divides the map into BUCKET_SIZE×BUCKET_SIZE tile cells.
 * Rebuilt once per game tick in O(n); queries run in O(k) where k is the
 * number of units in the queried buckets — typically ≪ n, especially in
 * sparse regions of the map.
 *
 * Two complementary lookups:
 *   • nearby(col, row, radius, out) — fills `out` with all units whose bucket
 *     overlaps a circle of `radius` tiles.  Over-inclusive by design: callers
 *     must still check exact distance, but bucket pre-filtering cuts the scan
 *     from O(n) to O(few) in practice.
 *   • atTile(col, row) — returns units standing on exactly this tile.
 *     Used by isOccupied checks, replaces the old activeUnits.some() scan.
 */

import type { Unit } from '../entities/Unit';

/** Side length (in tiles) of each spatial bucket. */
const BUCKET_SIZE = 4;

/**
 * Number of bits to shift the column-bucket index so row-bucket indices
 * fit in the low bits without collision.  Map is ≤ 128 tiles wide →
 * ≤ 32 buckets per axis → 5 bits is enough; we use 6 for safety.
 */
const BUCKET_SHIFT = 6; // 2^6 = 64 ≥ ceil(128 / BUCKET_SIZE)

/** Shared empty sentinel — returned by atTile() for unoccupied tiles. */
const EMPTY: readonly Unit[] = [];

export class SpatialIndex {
  /** Units keyed by their gridPos bucket (for radius queries). */
  private readonly buckets  = new Map<number, Unit[]>();
  /** Units keyed by their exact tile key (col * 256 + row, for atTile). */
  private readonly exactMap = new Map<number, Unit[]>();

  // ── Key helpers ────────────────────────────────────────────────────────

  private static bucketKey(col: number, row: number): number {
    return (Math.floor(col / BUCKET_SIZE) << BUCKET_SHIFT) |
            Math.floor(row / BUCKET_SIZE);
  }

  private static tileKey(col: number, row: number): number {
    // Map dimensions ≤ 192 — shift 8 gives 256 slots per axis, no collisions
    return (col << 8) | row;
  }

  // ── Build ──────────────────────────────────────────────────────────────

  /**
   * Rebuild both indexes from the live unit array.
   * Call once per game tick before combat / occupancy checks — O(n).
   */
  rebuild(units: Unit[]): void {
    this.buckets.clear();
    this.exactMap.clear();
    for (const unit of units) {
      if (!unit.alive) continue;
      SpatialIndex.insert(this.buckets,  SpatialIndex.bucketKey(unit.gridPos.col, unit.gridPos.row), unit);
      SpatialIndex.insert(this.exactMap, SpatialIndex.tileKey  (unit.gridPos.col, unit.gridPos.row), unit);
    }
  }

  private static insert(map: Map<number, Unit[]>, key: number, unit: Unit): void {
    let bucket = map.get(key);
    if (bucket === undefined) { bucket = []; map.set(key, bucket); }
    bucket.push(unit);
  }

  // ── Query ──────────────────────────────────────────────────────────────

  /**
   * Fill `out` with all alive units whose bucket overlaps a circle of
   * `radius` tiles centred on (col, row).  The result is over-inclusive —
   * callers should still check exact distance before using a candidate.
   * Writes into a pre-allocated array to avoid allocation on the hot path.
   */
  nearby(col: number, row: number, radius: number, out: Unit[]): void {
    out.length = 0;
    const bcX = Math.floor(col  / BUCKET_SIZE);
    const bcY = Math.floor(row  / BUCKET_SIZE);
    const br  = Math.ceil(radius / BUCKET_SIZE) + 1;
    for (let dx = -br; dx <= br; dx++) {
      for (let dy = -br; dy <= br; dy++) {
        const key    = ((bcX + dx) << BUCKET_SHIFT) | (bcY + dy);
        const bucket = this.buckets.get(key);
        if (bucket) for (const u of bucket) out.push(u);
      }
    }
  }

  /**
   * Return all alive units whose gridPos is exactly (col, row).
   * Returns a shared empty array when the tile is unoccupied — do not mutate.
   */
  atTile(col: number, row: number): readonly Unit[] {
    return this.exactMap.get(SpatialIndex.tileKey(col, row)) ?? EMPTY;
  }
}
